import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { BsPencil, BsTrash, BsKey, BsPersonPlus, BsCheckCircle, BsEnvelope, BsThreeDotsVertical, BsEye, BsEyeSlash } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import PortalModal from '../components/PortalModal';
import userService from '../api/userService';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', color: '#cce5ff' },
  { value: 'superadmin', label: 'Super Admin', color: '#f8d7da' },
  { value: 'viewer', label: 'Viewer', color: '#f2f8d7ff' },
];

const EMPTY_FORM = { username: '', email: '', password: '', role: 'admin', first_name: '', last_name: '', phone_number: '' };
const EMPTY_PWD = { new_password: '', confirm: '' };

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map(e => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
const PHONE_PATTERN = /^\+?[0-9() .-]{7,20}$/;
const fullName = (row) => [row.first_name, row.last_name].filter(Boolean).join(' ');

export default function UsersPage() {
  const isViewer = useSelector((s) => s.auth.user?.role) === 'viewer';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pwdForm, setPwdForm] = useState(EMPTY_PWD);
  const [formErrors, setFormErrors] = useState({});
  const [pwdErrors, setPwdErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [filters, setFilters] = useState({ search: '', role: '', page: 1, limit: 20 });

  const handleClearFilters = () => {
    setFilters({ search: '', role: '', page: 1, limit: 20 });
  };

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      const { data: res } = await userService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── User form ── */
  const openAdd = () => { setForm(EMPTY_FORM); setEditItem(null); setFormErrors({}); setShowPassword(false); setShowUserModal(true); };
  const openEdit = row => {
    setForm({
      username: row.username,
      email: row.email || '',
      password: '',
      role: row.role,
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      phone_number: row.phone_number || '',
    });
    setEditItem(row);
    setFormErrors({});
    setShowUserModal(true);
  };
  const closeUserModal = () => { setShowUserModal(false); setEditItem(null); };

  const setF = (k, v) => { setForm(f => ({ ...f, [k]: v })); setFormErrors(fe => ({ ...fe, [k]: '' })); };

  const validateUserForm = () => {
    const errors = {};
    if (!form.username) errors.username = 'Nom d\'utilisateur requis';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Adresse e-mail invalide';
    if (form.first_name && !NAME_PATTERN.test(form.first_name)) errors.first_name = 'Le prénom ne peut contenir que des lettres, espaces, apostrophes et tirets';
    if (form.last_name && !NAME_PATTERN.test(form.last_name)) errors.last_name = 'Le nom ne peut contenir que des lettres, espaces, apostrophes et tirets';
    if (form.phone_number && !PHONE_PATTERN.test(form.phone_number)) errors.phone_number = 'Numéro de téléphone invalide';
    // if (!editItem && !form.password) errors.password = 'Mot de passe requis';
    return errors;
  };

  const handleSaveUser = async () => {
    const errors = validateUserForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    setSaving(true);
    try {
      const payload = {
        username: form.username,
        email: form.email || null,
        role: form.role,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        phone_number: form.phone_number || null,
      };

      if (editItem) {
        await userService.update(editItem.id, payload);
        showToast('Utilisateur mis à jour avec succès');
      } else {
        await userService.create({ ...payload });
        showToast('Utilisateur créé avec succès');
      }
      closeUserModal(); fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setSaving(false); }
  };

  /* ── Password reset ── */
  const openPwd = row => { setPwdTarget(row); setPwdForm(EMPTY_PWD); setPwdErrors({}); setShowResetPassword(false); setShowResetConfirm(false); setShowPwdModal(true); };
  const closePwdModal = () => { setShowPwdModal(false); setPwdTarget(null); };

  const validatePwd = () => {
    const errors = {};
    if (!pwdForm.new_password) errors.new_password = 'Nouveau mot de passe requis';
    if (pwdForm.new_password !== pwdForm.confirm) errors.confirm = 'Les mots de passe ne correspondent pas';
    return errors;
  };

  const handleResetPwd = async () => {
    const errors = validatePwd();
    if (Object.keys(errors).length) { setPwdErrors(errors); return; }
    setSaving(true);
    try {
      await userService.resetPassword(pwdTarget.id, { new_password: pwdForm.new_password });
      showToast(`Mot de passe réinitialisé pour ${pwdTarget.username}`);
      closePwdModal();
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setSaving(false); }
  };

  /* ── Email verification ── */
  const openVerify = row => { setVerifyTarget(row); setShowVerifyModal(true); };
  const closeVerifyModal = () => { setShowVerifyModal(false); setVerifyTarget(null); };

  const handleSendVerification = async () => {
    setSaving(true);
    try {
      await userService.sendVerificationEmail(verifyTarget.id);
      showToast(`Email de vérification envoyé à ${verifyTarget.email}`);
      closeVerifyModal();
      fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setSaving(false); }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    try {
      await userService.remove(deleteTarget.id);
      showToast('Utilisateur supprimé avec succès');
      setDeleteTarget(null); fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); setDeleteTarget(null); }
  };

  const openDeleteConfirm = row => { setDeleteTarget(row); };
  const closeDeleteModal = () => { setDeleteTarget(null); };

  const roleLabel = v => ROLE_OPTIONS.find(r => r.value === v)?.label || v;
  const roleBg = v => ROLE_OPTIONS.find(r => r.value === v)?.color || '#eee';

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>GESTION DES UTILISATEURS</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4 d-flex flex-wrap gap-3 align-items-end" style={{ border: '1px solid #eee' }}>
        <div style={{ flex: '1 1 200px' }}>
          <Form.Label className="small fw-semibold d-block mb-1">Recherche</Form.Label>
          <Form.Control size="sm" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} placeholder="Nom d'utilisateur..." />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <Form.Label className="small fw-semibold d-block mb-1">Rôle</Form.Label>
          <Form.Select size="sm" value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value, page: 1 }))}>
            <option value="">Tous les rôles</option>
            {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Form.Select>
        </div>
        {['search', 'role'].some((k) => filters[k]) && (
          <Button size="sm" variant="outline-secondary" onClick={handleClearFilters} className="text-uppercase px-2">
            EFFACER
          </Button>
        )}
        {!isViewer && (
          <Button size="sm" onClick={openAdd} style={{ backgroundColor: '#8B0000', border: 'none', marginLeft: 'auto' }}>
            <BsPersonPlus size={14} className="me-1" /> Nouvel Utilisateur
          </Button>
        )}
      </div>

      {/* List */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">Résultats ({pagination.total ?? data.length})</h6>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8B0000' }} /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-5 text-secondary">Aucun utilisateur trouvé.</div>
      ) : (
        <>
          <div className="table-mcm-wrap d-none d-md-block" style={{ overflowX: 'auto' }}>
            <table className="table-mcm">
              <thead>
                <tr>
                  <th className="th-mcm">Nom d'utilisateur</th>
                  <th className="th-mcm">Nom complet</th>
                  <th className="th-mcm">E-mail</th>
                  <th className="th-mcm">Telephone</th>
                  <th className="th-mcm">Role</th>
                  <th className="th-mcm">Email verifie</th>
                  <th className="th-mcm">Cree le</th>
                  <th className="th-mcm">Modifie le</th>
                  {!isViewer && <th className="th-mcm">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row.id}>
                    <td className="td-mcm" style={{ fontWeight: 600 }}>{row.username}</td>
                    <td className="td-mcm" style={{ color: '#555' }}>{fullName(row) || '-'}</td>
                    <td className="td-mcm" style={{ color: '#555' }}>{row.email || '—'}</td>
                    <td className="td-mcm" style={{ color: '#555', whiteSpace: 'nowrap' }}>{row.phone_number || '-'}</td>
                    <td className="td-mcm">
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: roleBg(row.role), fontWeight: 600 }}>{roleLabel(row.role)}</span>
                    </td>
                    <td className="td-mcm">
                      {row.email_verified ? (
                        <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: '#d4edda', fontWeight: 600, color: '#155724', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                          <BsCheckCircle size={12} /> Vérifié
                        </span>
                      ) : (
                        <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: '#fff3cd', fontWeight: 600, color: '#856404', display: 'inline-block' }}>
                          En attente
                        </span>
                      )}
                    </td>
                    <td className="td-mcm" style={{ whiteSpace: 'nowrap', color: '#666' }}>{formatDate(row.created_at)}</td>
                    <td className="td-mcm" style={{ whiteSpace: 'nowrap', color: '#666' }}>{formatDate(row.updated_at)}</td>
                    <td className="td-mcm">
                      <div className="d-flex gap-2">
                        <BsPencil size={15} style={{ cursor: 'pointer', color: '#555' }} title="Modifier" onClick={() => openEdit(row)} />
                        {row.email && !row.email_verified && (
                          <BsEnvelope size={15} style={{ cursor: 'pointer', color: '#ff9800' }} title="Envoyer email de verification" onClick={() => openVerify(row)} />
                        )}
                        <BsKey size={15} style={{ cursor: 'pointer', color: '#1a73e8' }} title="Reinitialiser mot de passe" onClick={() => openPwd(row)} />
                        <BsTrash size={15} style={{ cursor: 'pointer', color: '#8B0000' }} title="Supprimer" onClick={() => openDeleteConfirm(row)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-md-none">
            <div className="d-flex flex-column gap-3">
              {data.map(row => (
                <div key={row.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                  <div className="d-flex justify-content-between border-bottom border-muted w-100 pb-3 align-items-start mb-3">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#8b1512', overflowWrap: 'anywhere' }}>{row.username}</div>
                      <div className="text-muted fw-semibold" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
                        {row.email || 'Aucun e-mail'}
                      </div>
                    </div>
                    <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: roleBg(row.role), fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 8 }}>
                      {roleLabel(row.role)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 10 }}>
                    {row.email_verified ? (
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: '#d4edda', fontWeight: 600, color: '#155724', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <BsCheckCircle size={12} /> Vérifié
                      </span>
                    ) : (
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, backgroundColor: '#fff3cd', fontWeight: 600, color: '#856404', display: 'inline-block' }}>
                        En attente
                      </span>
                    )}
                    {!isViewer && (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <BsThreeDotsVertical
                          onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)}
                          size={18}
                          color="#555"
                          style={{ cursor: 'pointer' }}
                        />
                        {openMenuId === row.id && (
                          <div
                            style={{
                              position: 'absolute', right: 0, top: '100%', zIndex: 100,
                              backgroundColor: '#fff', border: '1px solid #e0e0e0',
                              borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              minWidth: 190, overflow: 'hidden', marginTop: 4,
                            }}
                          >
                            <button
                              type="button"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                              onClick={() => { openEdit(row); setOpenMenuId(null); }}
                            >
                              <BsPencil size={13} color="#555" /> Modifier
                            </button>
                            {row.email && !row.email_verified && (
                              <button
                                type="button"
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                                onClick={() => { openVerify(row); setOpenMenuId(null); }}
                              >
                                <BsEnvelope size={13} color="#ff9800" /> Vérifier l'e-mail
                              </button>
                            )}
                            <button
                              type="button"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                              onClick={() => { openPwd(row); setOpenMenuId(null); }}
                            >
                              <BsKey size={13} color="#1a73e8" /> Réinitialiser
                            </button>
                            <button
                              type="button"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8B0000', cursor: 'pointer' }}
                              onClick={() => { openDeleteConfirm(row); setOpenMenuId(null); }}
                            >
                              <BsTrash size={13} color="#8B0000" /> Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: 13 }}>
                    <div className="mb-1"><span className="fw-semibold">Nom complet : </span>{fullName(row) || '-'}</div>
                    <div className="mb-1"><span className="fw-semibold">Téléphone : </span>{row.phone_number || '-'}</div>
                    <div className="mb-1"><span className="fw-semibold">Créé le : </span>{formatDate(row.created_at)}</div>
                    <div><span className="fw-semibold">Modifié le : </span>{formatDate(row.updated_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Pagination
        currentPage={filters.page}
        totalPages={pagination.totalPages}
        onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
      />

      {/* All Modals - Always Rendered to Prevent Portal Issues */}

      {/* User Modal */}
      <PortalModal show={showUserModal} onClose={closeUserModal} size="md">
        <button onClick={closeUserModal} className="modal-close-btn">×</button>
        <h6 style={{ fontWeight: 700, marginBottom: 20 }}>{editItem ? `Modifier — ${editItem.username}` : 'Nouvel utilisateur'}</h6>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Nom d'utilisateur :</Form.Label>
          <Form.Control value={form.username} onChange={e => setF('username', e.target.value)} placeholder="admin_jean" isInvalid={!!formErrors.username} style={{ marginTop: 4 }} />
          <Form.Control.Feedback type="invalid">{formErrors.username}</Form.Control.Feedback>
        </Form.Group>

        <div className="d-flex gap-3 flex-wrap">
          <Form.Group className="mb-3" style={{ flex: '1 1 180px' }}>
            <Form.Label className="modal-label">Prénom :</Form.Label>
            <Form.Control value={form.first_name} onChange={e => setF('first_name', e.target.value)} placeholder="Jean" isInvalid={!!formErrors.first_name} style={{ marginTop: 4 }} />
            <Form.Control.Feedback type="invalid">{formErrors.first_name}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" style={{ flex: '1 1 180px' }}>
            <Form.Label className="modal-label">Nom de famille :</Form.Label>
            <Form.Control value={form.last_name} onChange={e => setF('last_name', e.target.value)} placeholder="Nom de famille" isInvalid={!!formErrors.last_name} style={{ marginTop: 4 }} />
            <Form.Control.Feedback type="invalid">{formErrors.last_name}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">E-mail :</Form.Label>
          <Form.Control type="email" value={form.email} onChange={e => setF('email', e.target.value)} placeholder="utilisateur@exemple.com" isInvalid={!!formErrors.email} style={{ marginTop: 4 }} />
          <Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Téléphone :</Form.Label>
          <Form.Control type="tel" value={form.phone_number} onChange={e => setF('phone_number', e.target.value)} placeholder="+225 07 12 34 56 78" isInvalid={!!formErrors.phone_number} style={{ marginTop: 4 }} />
          <Form.Control.Feedback type="invalid">{formErrors.phone_number}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Rôle :</Form.Label>
          <Form.Select value={form.role} onChange={e => setF('role', e.target.value)} style={{ marginTop: 4 }}>
            {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Form.Select>
        </Form.Group>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="btn-primary-mcm" onClick={handleSaveUser} disabled={saving}
            style={{ minWidth: 180, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? <Spinner size="sm" animation="border" /> : editItem ? 'ENREGISTRER' : 'CRÉER'}
          </button>
        </div>
      </PortalModal>

      {/* Password Reset Modal */}
      <PortalModal show={showPwdModal} onClose={closePwdModal} size="md">
        <button onClick={closePwdModal} className="modal-close-btn">×</button>
        <h6 style={{ fontWeight: 700, marginBottom: 20 }}>Réinitialiser le mot de passe — {pwdTarget?.username}</h6>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Nouveau mot de passe :</Form.Label>
          <div className={`d-flex align-items-center border rounded px-2 ${pwdErrors.new_password ? 'border-danger' : ''}`} style={{ marginTop: 4 }}>
            <Form.Control
              type={showResetPassword ? 'text' : 'password'}
              value={pwdForm.new_password}
              onChange={e => { setPwdForm(f => ({ ...f, new_password: e.target.value })); setPwdErrors(fe => ({ ...fe, new_password: '' })); }}
              placeholder="Min. 8 caractères, 1 chiffre, 1 spécial"
              style={{ border: 'none', boxShadow: 'none', paddingLeft: 0 }}
            />
            <button
              type="button"
              onClick={() => setShowResetPassword((v) => !v)}
              aria-label={showResetPassword ? 'Masquer le nouveau mot de passe' : 'Afficher le nouveau mot de passe'}
              style={{ border: 'none', background: 'none', color: '#888', cursor: 'pointer', padding: '6px 4px', flexShrink: 0 }}
            >
              {showResetPassword ? <BsEyeSlash size={18} /> : <BsEye size={18} />}
            </button>
          </div>
          {pwdErrors.new_password && <div className="invalid-feedback d-block">{pwdErrors.new_password}</div>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Confirmer le mot de passe :</Form.Label>
          <div className={`d-flex align-items-center border rounded px-2 ${pwdErrors.confirm ? 'border-danger' : ''}`} style={{ marginTop: 4 }}>
            <Form.Control
              type={showResetConfirm ? 'text' : 'password'}
              value={pwdForm.confirm}
              onChange={e => { setPwdForm(f => ({ ...f, confirm: e.target.value })); setPwdErrors(fe => ({ ...fe, confirm: '' })); }}
              placeholder="Répéter le mot de passe"
              style={{ border: 'none', boxShadow: 'none', paddingLeft: 0 }}
            />
            <button
              type="button"
              onClick={() => setShowResetConfirm((v) => !v)}
              aria-label={showResetConfirm ? 'Masquer la confirmation du mot de passe' : 'Afficher la confirmation du mot de passe'}
              style={{ border: 'none', background: 'none', color: '#888', cursor: 'pointer', padding: '6px 4px', flexShrink: 0 }}
            >
              {showResetConfirm ? <BsEyeSlash size={18} /> : <BsEye size={18} />}
            </button>
          </div>
          {pwdErrors.confirm && <div className="invalid-feedback d-block">{pwdErrors.confirm}</div>}
        </Form.Group>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="btn-primary-mcm" onClick={handleResetPwd} disabled={saving}
            style={{ minWidth: 180, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? <Spinner size="sm" animation="border" /> : 'RÉINITIALISER'}
          </button>
        </div>
      </PortalModal>

      {/* Delete Modal */}
      <PortalModal show={!!deleteTarget} onClose={closeDeleteModal} size="sm">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
            <BsTrash size={22} color="#8B0000" />
          </div>
          <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cet utilisateur ?</h6>
          <p style={{ fontSize: 13, color: '#e65100', margin: 0, fontWeight: 600 }}>⚠ Toutes ses sessions seront révoquées.</p>
        </div>

        <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
          <div className="mb-2">
            <span className="fw-semibold" style={{ fontSize: 14 }}>{deleteTarget?.username}</span>
          </div>
          {deleteTarget?.email && (
            <div className="mb-2"><span className="fw-semibold">E-mail : </span>{deleteTarget.email}</div>
          )}
          <div>
            <span className="fw-semibold">Rôle : </span>
            <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: roleBg(deleteTarget?.role) }}>
              {roleLabel(deleteTarget?.role)}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={closeDeleteModal} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>ANNULER</button>
          <button onClick={handleDelete} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>SUPPRIMER</button>
        </div>
      </PortalModal>

      {/* Email Verification Modal */}
      <PortalModal show={showVerifyModal} onClose={closeVerifyModal} size="md">
        <button onClick={closeVerifyModal} className="modal-close-btn">×</button>
        <h6 style={{ fontWeight: 700, marginBottom: 20 }}>Envoyer email de vérification</h6>

        <div style={{ backgroundColor: '#f9f9f9', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
          <div className="mb-2">
            <span className="fw-semibold" style={{ fontSize: 14 }}>{verifyTarget?.username}</span>
          </div>
          <div className="mb-2">
            <span className="fw-semibold">E-mail : </span>{verifyTarget?.email}
          </div>
        </div>

        <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
          Un email de vérification sera envoyé à <strong>{verifyTarget?.email}</strong>.
          L'utilisateur devra cliquer sur le lien d'activation dans l'email pour vérifier son adresse (valable 24 heures).
        </p>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="btn-primary-mcm" onClick={handleSendVerification} disabled={saving}
            style={{ minWidth: 180, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? <Spinner size="sm" animation="border" /> : 'ENVOYER'}
          </button>
        </div>
      </PortalModal>
    </div>
  );
}



